import {
  createRouter,
  createWebHashHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import { hasPagePermission } from "@/core/services/permission.service";
import { VZ_PERMISSIONS } from "@/helpers/permissionMapper";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/branch-dashboard",
    component: () => import("@/layout/Layout.vue"),
    beforeEnter: () => {
      store.dispatch(Actions.VERIFY_AUTH);
      if (!store.getters.isUserAuthenticated) {
        return "/sign-in";
      }
      return store.getters.isUserAuthenticated;
    },
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        redirect: "/branch-dashboard",
      },
      {
        path: "/form-builder/:id/perview",
        name: "form.perview",
        component: () => import("@/views/formBuilder/FormBuilderPreview.vue"),
      },
      {
        path: "/form-builder/:id",
        name: "form.builder",
        component: () => import("@/views/formBuilder/FormBuilderIndex.vue"),
      },
      {
        path: "/lookup",
        name: "lookup",
        component: () => import("@/views/lookup/LookupIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.SETTINGS_LOOKUPCONFIGURATION,
        },
        children: [
          {
            path: "/lookup/detail",
            name: "lookup.detail",
            component: () => import("@/views/lookup/views/LookupDetail.vue"),
          },
          {
            path: "/lookup/datamodel",
            name: "lookup.datamodel",
            component: () => import("@/views/lookup/views/LookupDatamodel.vue"),
          },
        ],
      },
      {
        path: "/eventManagement",
        name: "eventManagement",
        component: () =>
          import("@/views/eventManagement/eventManagementIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.SETTINGS_EVENTS,
        },
        children: [
          {
            path: "/eventManagement/detail",
            name: "eventManagement.detail",
            component: () =>
              import("@/views/eventManagement/views/EventDetail.vue"),
          },
          {
            path: "/eventManagement/form",
            name: "eventManagement.form",
            component: () =>
              import("@/views/eventManagement/views/EventManagementForm.vue"),
          },
        ],
      },
      {
        path: "detail",
        name: "detail",
        component: () => import("@/components/content/VZDetailPage.vue"),
        children: [
          {
            path: "site/:id",
            name: "detail.site",
            component: () => import("@/views/site/SiteDetail.vue"),
            meta: {
              close: "site",
              PERMISSION: VZ_PERMISSIONS.BUSINESS_SITE,
            },
          },
          {
            path: "form/:id",
            name: "detail.form",
            component: () => import("@/views/form/FormDetail.vue"),
            meta: {
              close: "form",
              PERMISSION: VZ_PERMISSIONS.TEMPLATES_FORMS,
            },
          },
          {
            path: "user/:id",
            name: "detail.user",
            component: () => import("@/views/user/UserDetail.vue"),
            meta: {
              close: "user",
              PERMISSION: VZ_PERMISSIONS.USERS_USERS,
            },
          },
          {
            path: "device/:id",
            name: "detail.device",
            component: () => import("@/views/site/DeviceDetail.vue"),
            meta: {
              close: "devices",
              PERMISSION: VZ_PERMISSIONS.INTEGRATIONS_DEVICES,
            },
          },
          {
            path: "event/:id",
            name: "detail.event",
            component: () => import("@/views/eventManagement/EventDetail.vue"),
            meta: {
              close: "eventManagement",
              PERMISSION: VZ_PERMISSIONS.SETTINGS_EVENTS,
            },
          },
          {
            path: "location/:id",
            name: "detail.location",
            component: () =>
              import("@/views/location/views/LocationDetailView.vue"),
            meta: {
              close: "locations",
              PERMISSION: VZ_PERMISSIONS.BUSINESS_LOCATIONS,
            },
          },
          {
            path: "/role",
            name: "detail.roles",
            component: () => import("@/views/user/UserRolesAndPermission.vue"),
            meta: {
              close: "roles",
              PERMISSION: VZ_PERMISSIONS.USERS_ROLES,
            },
          },
        ],
      },
      {
        path: "/branch-dashboard",
        name: "branch-dashboard",
        component: () => import("@/views/site/siteDashboard.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.DASHBOARD_SITESOVERVIEW,
        },
      },
      {
        path: "/home",
        name: "home",
        component: () => import("@/views/home/Index.vue"),
      },
      {
        path: "/branch",
        name: "branch",
        component: () => import("@/views/site/SiteIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.BUSINESS_SITE,
        },
        children: [
          {
            path: "/branch",
            name: "site",
            component: () => import("@/views/site/views/SiteUser.vue"),
          },
          {
            path: "/branch/form",
            name: "site.form",
            component: () => import("@/views/site/views/SiteForms.vue"),
          },
          {
            path: "/branch/activity",
            name: "site.activity",
            component: () => import("@/views/site/views/SiteActivity.vue"),
          },
          {
            path: "/branch/device",
            name: "site.device",
            component: () => import("@/views/site/views/SiteDevice.vue"),
          },
        ],
      },
      {
        path: "/devices",
        name: "devices",
        component: () => import("@/views/site/SiteDevices.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.INTEGRATIONS_DEVICES,
        },
        children: [
          {
            path: "/devices/site",
            name: "devices.site",
            component: () => import("@/views/site/views/DeviceSite.vue"),
          },
        ],
      },
      {
        path: "/form-dashboard",
        name: "form-dashboard",
        component: () => import("@/views/form/formDashboard.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.DASHBOARD_PERFORMANCEREVIEW,
        },
      },
      {
        path: "/form",
        name: "form.definition",
        component: () => import("@/views/form/FormIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.TEMPLATES_FORMS,
        },
        children: [
          {
            path: "/form/detail",
            name: "form.detail",
            component: () => import("@/views/form/views/FormDetail.vue"),
          },
          {
            path: "/form/datamodel",
            name: "form.datamodel",
            component: () => import("@/views/form/views/FormDatamodel.vue"),
          },
          {
            path: "/form/site",
            name: "form.site",
            component: () => import("@/views/form/views/FormSite.vue"),
          },
          {
            path: "/form/user",
            name: "form.user",
            component: () => import("@/views/form/views/FormUser.vue"),
          },
          {
            path: "/form/event",
            name: "form.event",
            component: () => import("@/views/form/views/FormEvent.vue"),
          },
          {
            path: "/form/activity",
            name: "form.activity",
            component: () => import("@/views/form/views/FormActivity.vue"),
          },
          {
            path: "/form/category",
            name: "form.category",
            component: () => import("@/views/form/views/FormCategory.vue"),
          },
          {
            path: "/form/routing",
            name: "routing",
            component: () => import("@/views/form/views/FormRouting.vue"),
          },
          {
            path: "/form/schedule",
            name: "form.schedule",
            component: () => import("@/views/form/views/FormSchedule.vue"),
          },
        ],
      },
      {
        path: "/sections",
        name: "sections",
        component: () => import("@/views/form/FormSections.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.TEMPLATES_SECTIONS,
        },
      },
      {
        path: "/locations",
        name: "locations",
        component: () => import("@/views/location/LocationIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.BUSINESS_LOCATIONS,
        },
      },
      {
        path: "/accountsettings",
        name: "accountsettings",
        component: () => import("@/views/account/AccountSettings.vue"),
      },
      {
        path: "/email/dashboard",
        name: "email.dashboard",
        component: () => import("@/views/email/views/EmailDashboard.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.APPLICATIONS_EMAIL,
        },
      },
      {
        path: "/user-dashboard",
        name: "user-dashboard",
        component: () => import("@/views/user/userDashboard.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.DASHBOARD_USERSTATISTICS,
        },
      },
      {
        path: "/user",
        name: "user",
        component: () => import("@/views/user/UserIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.USERS_USERS,
        },
      },
      {
        path: "/user/permissionGroup",
        name: "user.permissionGroup",
        component: () => import("@/views/user/views/UserPermissionGroup.vue"),

        children: [
          {
            path: "/user/detail",
            name: "user.detail",
            component: () => import("@/views/user/views/UserDetail.vue"),
          },
          {
            path: "/user/permissionGroup",
            name: "user.permissionGroup",
            component: () =>
              import("@/views/user/views/UserPermissionGroup.vue"),
          },
          {
            path: "/user/role",
            name: "user.role",
            component: () => import("@/views/user/views/UserRole.vue"),
          },
          {
            path: "/user/site",
            name: "user.site",
            component: () => import("@/views/user/views/UserSite.vue"),
          },
          {
            path: "/user/form",
            name: "user.form",
            component: () => import("@/views/user/views/UserForm.vue"),
          },
          {
            path: "/user/activity",
            name: "user.activity",
            component: () => import("@/views/user/views/UserActivity.vue"),
          },
          {
            path: "/user/changePassword",
            name: "user.changePassword",
            component: () =>
              import("@/views/user/views/UserChangePassword.vue"),
          },
        ],
      },
      {
        path: "/roles",
        name: "roles",
        component: () => import("@/views/user/UserRolesAndPermission.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.USERS_ROLES,
        },
      },
      {
        path: "/client",
        name: "clients",
        component: () => import("@/views/site/views/ClientDetails.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.BUSINESS_CLIENT,
        },
      },
      {
        path: "/smsConfig",
        name: "smsConfig",
        component: () => import("@/views/sms/views/SmsConfiguration.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.INTEGRATIONS_SMSMANAGER,
        },
      },
      {
        path: "/documents",
        name: "documents",
        component: () => import("@/views/site/views/Documents.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.TEMPLATES_TASKS,
        },
      },
      {
        path: "/permissions",
        name: "permissions",
        component: () => import("@/views/user/Permissions.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.USERS_PERMISSIONS,
        },
      },
      {
        path: "/user-permission",
        name: "user.permission",
        component: () => import("@/views/user/views/UserPermission.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.USERS_USERPERMISSIONS,
        },
      },
      {
        path: "/email",
        name: "email",
        component: () => import("@/views/email/EmailIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.INTEGRATIONS_EMAILMANAGER,
        },
      },
      {
        path: "/chat",
        redirect: "/chat",
        component: () => import("@/views/sms/SMSIndex.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.APPLICATIONS_SMS,
        },
        children: [
          {
            path: "/chat",
            name: "sms",
            component: () => import("@/views/sms/views/Index.vue"),
          },
          {
            path: ":id",
            name: "sms.detail",
            component: () => import("@/views/sms/views/Index.vue"),
          },
        ],
      },
      {
        path: "/email/dashboard",
        name: "email.dashboard",
        component: () => import("@/views/email/views/EmailDashboard.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.APPLICATIONS_EMAIL,
        },
        children: [
          // {
          //   path: "/email/dashboard",
          //   name: "email.dashboard",
          //   component: () => import("@/views/email/views/EmailDashboard.vue"),
          // },
          {
            path: "/email/details",
            name: "email.details",
            component: () => import("@/views/email/views/EmailDetail.vue"),
          },
        ],
      },
      {
        path: "/scheduler",
        name: "taskscheduler",
        component: () => import("@/views/schedular/TaskScheduler.vue"),
        meta: {
          PERMISSION: VZ_PERMISSIONS.APPLICATIONS_SCHEDULER,
        },
      },
      {
        name: "schedulerSingle",
        component: () => import("@/views/schedular/SchedulerSingle.vue"),
        path: "/scheduler-single/:siteId/:userId/",
        meta: {
          PERMISSION: VZ_PERMISSIONS.APPLICATIONS_SCHEDULER,
        },
      },
      {
        path: "/report",
        name: "report",
        component: () => import("@/views/report/ReportIndex.vue"),
      },
      // {
      //   path: "/crafted/pages/profile",
      //   name: "profile",
      //   component: () => import("@/components/page-layouts/Profile.vue"),
      //   children: [
      //     // {
      //     //   path: "overview",
      //     //   name: "profile-overview",
      //     //   component: () =>
      //     //     import("@/views/crafted/pages/profile/Overview.vue"),
      //     // },
      //     // {
      //     //   path: "projects",
      //     //   name: "profile-projects",
      //     //   component: () =>
      //     //     import("@/views/crafted/pages/profile/Projects.vue"),
      //     // },
      //     // {
      //     //   path: "campaigns",
      //     //   name: "profile-campaigns",
      //     //   component: () =>
      //     //     import("@/views/crafted/pages/profile/Campaigns.vue"),
      //     // },
      //     {
      //       path: "documents",
      //       name: "profile-documents",
      //       component: () =>
      //         import("@/views/crafted/pages/profile/Documents.vue"),
      //     },
      //     // {
      //     //   path: "connections",
      //     //   name: "profile-connections",
      //     //   component: () =>
      //     //     import("@/views/crafted/pages/profile/Connections.vue"),
      //     // },
      //     {
      //       path: "activity",
      //       name: "profile-activity",
      //       component: () =>
      //         import("@/views/crafted/pages/profile/Activity.vue"),
      //     },
      //   ],
      // },
      // {
      //   path: "/crafted/pages/wizards/horizontal",
      //   name: "horizontal-wizard",
      //   component: () => import("@/views/crafted/pages/wizards/Horizontal.vue"),
      // },
      // {
      //   path: "/comingSoon",
      //   name: "horizontal-wizard",
      //   component: () =>
      //     import("@/views/crafted/pages/coming-soon/ComingSoon.vue"),
      // },
      // {
      //   path: "/crafted/pages/wizards/vertical",
      //   name: "vertical-wizard",
      //   component: () => import("@/views/crafted/pages/wizards/Vertical.vue"),
      // },
      // {
      //   path: "/crafted/account",
      //   name: "account",
      //   component: () => import("@/views/crafted/account/Account.vue"),
      //   children: [
      //     {
      //       path: "overview",
      //       name: "account-overview",
      //       component: () => import("@/views/crafted/account/Overview.vue"),
      //     },
      //     // {
      //     //   path: "settings",
      //     //   name: "account-settings",
      //     //   component: () => import("@/views/crafted/account/Settings.vue"),
      //     // },
      //   ],
      // },
      // {
      //   path: "/apps/customers/getting-started",
      //   name: "apps-customers-getting-started",
      //   component: () => import("@/views/apps/customers/GettingStarted.vue"),
      // },
      // {
      //   path: "/apps/customers/customers-listing",
      //   name: "apps-customers-listing",
      //   component: () => import("@/views/apps/customers/CustomersListing.vue"),
      // },
      // {
      //   path: "/apps/customers/customer-details",
      //   name: "apps-customers-details",
      //   component: () => import("@/views/apps/customers/CustomerDetails.vue"),
      // },
      // {
      //   path: "/subscriptions/getting-started",
      //   name: "subscriptions-getting-started",
      //   component: () =>
      //     import("@/views/apps/subscriptions/GettingStarted.vue"),
      // },
      // {
      //   path: "/subscriptions/subscription-list",
      //   name: "subscriptions-subscription-list",
      //   component: () =>
      //     import("@/views/apps/subscriptions/SubscriptionList.vue"),
      // },
      // {
      //   path: "/subscriptions/add-subscription",
      //   name: "subscriptions-add-subscription",
      //   component: () =>
      //     import("@/views/apps/subscriptions/AddSubscription.vue"),
      // },
      // {
      //   path: "/subscriptions/view-subscription",
      //   name: "subscriptions-view-subscription",
      //   component: () =>
      //     import("@/views/apps/subscriptions/ViewSubscription.vue"),
      // },
      // {
      //   path: "/apps/calendar",
      //   name: "apps-calendar",
      //   component: () => import("@/views/apps/Calendar.vue"),
      // },
      // {
      //   path: "/apps/chat/private-chat",
      //   name: "apps-private-chat",
      //   component: () => import("@/views/apps/chat/Chat.vue"),
      // },
      // {
      //   path: "/apps/chat/group-chat",
      //   name: "apps-group-chat",
      //   component: () => import("@/views/apps/chat/Chat.vue"),
      // },
      // {
      //   path: "/apps/chat/drawer-chat",
      //   name: "apps-drawer-chat",
      //   component: () => import("@/views/apps/chat/DrawerChat.vue"),
      // },
      // {
      //   path: "/crafted/modals/general/invite-friends",
      //   name: "modals-general-invite-friends",
      //   component: () =>
      //     import("@/views/crafted/modals/general/InviteFriends.vue"),
      // },
      // {
      //   path: "/crafted/modals/general/view-user",
      //   name: "modals-general-view-user",
      //   component: () => import("@/views/crafted/modals/general/ViewUsers.vue"),
      // },
      // {
      //   path: "/crafted/modals/general/upgrade-plan",
      //   name: "modals-general-upgrade-plan",
      //   component: () =>
      //     import("@/views/crafted/modals/general/UpgradePlan.vue"),
      // },
      // {
      //   path: "/crafted/modals/general/share-and-earn",
      //   name: "modals-general-share-and-earn",
      //   component: () =>
      //     import("@/views/crafted/modals/general/ShareAndEarn.vue"),
      // },
      // {
      //   path: "/crafted/modals/forms/new-target",
      //   name: "modals-forms-new-target",
      //   component: () => import("@/views/crafted/modals/forms/NewTarget.vue"),
      // },
      // {
      //   path: "/crafted/modals/forms/new-card",
      //   name: "modals-forms-new-card",
      //   component: () => import("@/views/crafted/modals/forms/NewCard.vue"),
      // },
      // {
      //   path: "/crafted/modals/forms/new-address",
      //   name: "modals-forms-new-address",
      //   component: () => import("@/views/crafted/modals/forms/NewAddress.vue"),
      // },
      // {
      //   path: "/crafted/modals/forms/create-api-key",
      //   name: "modals-forms-create-api-key",
      //   component: () =>
      //     import("@/views/crafted/modals/forms/CreateApiKey.vue"),
      // },
      // {
      //   path: "/crafted/modals/wizards/two-factor-auth",
      //   name: "modals-wizards-two-factor-auth",
      //   component: () =>
      //     import("@/views/crafted/modals/wizards/TwoFactorAuth.vue"),
      // },
      // {
      //   path: "/crafted/modals/wizards/create-app",
      //   name: "modals-wizards-create-app",
      //   component: () => import("@/views/crafted/modals/wizards/CreateApp.vue"),
      // },
      // {
      //   path: "/crafted/modals/wizards/create-account",
      //   name: "modals-wizards-create-account",
      //   component: () =>
      //     import("@/views/crafted/modals/wizards/CreateAccount.vue"),
      // },
      // {
      //   path: "/crafted/widgets/lists",
      //   name: "widgets-list",
      //   component: () => import("@/views/crafted/widgets/Lists.vue"),
      // },
      // {
      //   path: "/crafted/widgets/statistics",
      //   name: "widgets-statistics",
      //   component: () => import("@/views/crafted/widgets/Statistics.vue"),
      // },
      // {
      //   path: "/crafted/widgets/charts",
      //   name: "widgets-charts",
      //   component: () => import("@/views/crafted/widgets/Charts.vue"),
      // },
      // {
      //   path: "/crafted/widgets/mixed",
      //   name: "widgets-mixed",
      //   component: () => import("@/views/crafted/widgets/Mixed.vue"),
      // },
      // {
      //   path: "/crafted/widgets/tables",
      //   name: "widgets-tables",
      //   component: () => import("@/views/crafted/widgets/Tables.vue"),
      // },
      // {
      //   path: "/crafted/widgets/feeds",
      //   name: "widgets-feeds",
      //   component: () => import("@/views/crafted/widgets/Feeds.vue"),
      // },
    ],
  },
  {
    path: "/",
    component: () => import("@/components/page-layouts/Auth.vue"),
    beforeEnter: () => {
      if (store.getters.isUserAuthenticated) {
        return "/home";
      }
    },
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignIn.vue"),
      },
      {
        path: "/sign-up",
        name: "sign-up",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignUp.vue"),
      },
      {
        path: "/password-reset",
        name: "password-reset",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/PasswordReset.vue"),
      },
    ],
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import("@/views/crafted/authentication/Error404.vue"),
    beforeEnter: () => {
      store.dispatch(Actions.VERIFY_AUTH);
      if (!store.getters.isUserAuthenticated) {
        return "/sign-in";
      }
      return store.getters.isUserAuthenticated;
    },
  },
  {
    path: "/500",
    name: "500",
    component: () => import("@/views/crafted/authentication/Error500.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach((route: RouteLocationNormalized) => {
  // reset config to initial state
  const elements = document.querySelectorAll("#menu-section-3");
  elements.forEach((el) => {
    const a = el.querySelector("a");
    if (a) {
      const href = a.getAttribute("href");
      if (href && href.startsWith("#")) {
        const routePath = href.substring(1);
        if (routePath === "/sections" && route.path === "/sections") {
          a.classList.add("router-link-active", "router-link-exact-active");
        } else {
          a.classList.remove("router-link-active", "router-link-exact-active");
        }
      }
    }
  });
  store.commit(Mutations.RESET_LAYOUT_CONFIG);
  // store.dispatch(Actions.VERIFY_AUTH);
  if (!hasPagePermission(route)) {
    router.push("/404");
  }
  // Scroll page to top on every route change
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);
});

export default router;
